import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiAlertTriangle, FiX, FiCalendar, FiTool, FiArrowRight } = FiIcons;

const GarageNotification = () => {
  const [alerts, setAlerts] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const checkGarage = () => {
      const savedData = localStorage.getItem('bcb_user_vehicles');
      if (!savedData) return;

      try {
        const vehicles = JSON.parse(savedData);
        const newAlerts = [];

        vehicles.forEach(vehicle => {
          const vehicleAlerts = [];

          // Check Service
          if (vehicle.serviceDate) {
            const lastService = new Date(vehicle.serviceDate);
            const nextService = new Date(lastService);
            nextService.setFullYear(lastService.getFullYear() + 1);
            
            const now = new Date();
            now.setHours(0, 0, 0, 0);
            nextService.setHours(0, 0, 0, 0);

            if (now >= nextService) {
              vehicleAlerts.push({
                type: 'service',
                date: nextService.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }),
                label: 'Service fällig'
              });
            }
          }

          // Check TÜV
          if (vehicle.tuvMonth && vehicle.tuvYear) {
            const nextYear = parseInt(vehicle.tuvYear) + 2;
            const nextMonth = parseInt(vehicle.tuvMonth);
            
            const now = new Date();
            const currentYear = now.getFullYear();
            const currentMonth = now.getMonth() + 1;

            // Overdue logic consistent with Dashboard
            const isOverdue = (currentYear > nextYear) || (currentYear === nextYear && currentMonth > nextMonth);

            if (isOverdue) {
              vehicleAlerts.push({
                type: 'tuv',
                date: `${vehicle.tuvMonth}/${nextYear}`,
                label: 'TÜV abgelaufen'
              });
            }
          }

          if (vehicleAlerts.length > 0) {
            newAlerts.push({
              vehicle: `${vehicle.brand} ${vehicle.model}`,
              issues: vehicleAlerts
            });
          }
        });

        if (newAlerts.length > 0) {
          setAlerts(newAlerts);
          // Optional: Check sessionStorage to show only once per session if desired
          // For now, we show it every time the app loads if there are alerts, as requested.
          setIsOpen(true);
        }
      } catch (e) {
        console.error("Fehler bei der Garagen-Überprüfung", e);
      }
    };

    // Small delay to ensure smooth initial render
    const timer = setTimeout(checkGarage, 1000);
    return () => clearTimeout(timer);
  }, []);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={() => setIsOpen(false)}
        >
          <motion.div
            initial={{ y: 50, opacity: 0, scale: 0.95 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 50, opacity: 0, scale: 0.95 }}
            className="bg-gray-800 border border-orange-500/50 w-full max-w-md rounded-2xl shadow-2xl overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-orange-600 to-orange-500 p-4 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <div className="bg-white/20 p-2 rounded-lg">
                  <SafeIcon icon={FiAlertTriangle} className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white">Wartung fällig!</h3>
              </div>
              <button 
                onClick={() => setIsOpen(false)}
                className="text-white/80 hover:text-white hover:bg-white/20 rounded-lg p-1 transition-colors"
              >
                <SafeIcon icon={FiX} className="w-6 h-6" />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
              <p className="text-gray-300 text-sm">
                Für folgende Fahrzeuge in Ihrer Garage stehen Termine an:
              </p>
              
              <div className="space-y-3">
                {alerts.map((alert, idx) => (
                  <div key={idx} className="bg-gray-700/50 rounded-xl p-4 border border-gray-600">
                    <h4 className="font-bold text-white mb-3 border-b border-gray-600 pb-2">
                      {alert.vehicle}
                    </h4>
                    <div className="space-y-2">
                      {alert.issues.map((issue, i) => (
                        <div key={i} className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2 text-red-400 font-medium">
                            <SafeIcon 
                              icon={issue.type === 'service' ? FiTool : FiCalendar} 
                              className="w-4 h-4" 
                            />
                            <span>{issue.label}</span>
                          </div>
                          <span className="text-gray-400 bg-gray-800 px-2 py-0.5 rounded text-xs">
                            {issue.date}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 bg-gray-900 border-t border-gray-700 flex gap-3">
              <button
                onClick={() => setIsOpen(false)}
                className="flex-1 py-2.5 text-gray-400 font-medium hover:text-white transition-colors"
              >
                Später erinnern
              </button>
              <button
                onClick={() => {
                  setIsOpen(false);
                  navigate('/kontakt');
                }}
                className="flex-[2] py-2.5 bg-gradient-to-r from-orange-600 to-orange-500 text-white rounded-xl font-bold hover:from-orange-700 hover:to-orange-600 transition-all shadow-lg shadow-orange-500/20 flex items-center justify-center gap-2"
              >
                <span>Termin vereinbaren</span>
                <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default GarageNotification;